import * as React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Copyright from '../Copyright';
import MonitoringQuantityChart from '../AmlDashboard/MonitoringQuantityChart';
import MonitoringValueChart from '../AmlDashboard/MonitotingValueChart';
import RiskQuantityChart from '../AmlDashboard/RiskQuantityChart';
import RiskValueChart from '../AmlDashboard/RiskValueChart';
import StatCard from '../AmlDashboard/StatCard';
import { canViewTransactions } from '../../helpers/authSession';
import { useSelector } from 'react-redux';
import { rulesEngineGet } from '../../services/rulesEngineClient';
import { getRulesEngineUiMessage } from '../../services/rulesEngineErrors';
import { getRulesEngineAnalyticsUrl } from '../../services/rulesEngineEndpoints';
import { getFilterConfig } from '../../helpers/filterLabelGenerator';
import {
  ANALYSIS_STATUS_BY_MONITORING_CARD_ID,
  AML_DASHBOARD_GRID_TEXT,
  getApiDateRangeFromFilterState,
  getApiGroupByFromFilterState,
} from '../../helpers/transactionMetrics';

function formatDate(value) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatHourBucket(value) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const hour = String(value.getHours()).padStart(2, '0');
  return `${year}-${month}-${day} ${hour}:00:00`;
}

function parseRangeValue(value, boundary = 'start') {
  if (typeof value !== 'string' || !value)
    return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value))
    return new Date(`${value}T${boundary === 'end' ? '23:59:59' : '00:00:00'}`);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildSeries(value, length) {
  return Array.from({ length }, () => Number(value) || 0);
}

function formatCount(value) {
  return new Intl.NumberFormat('pt-BR').format(Number(value) || 0);
}

const brlTooltipFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

function formatBrlTooltipValue(item) {
  return brlTooltipFormatter.format(Number(item?.value ?? item ?? 0));
}

function toFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function getHistoryVolumeValue(item) {
  return toFiniteNumber(item?.volume_with_alerts_raw)
    ?? toFiniteNumber(item?.total_volume_raw)
    ?? 0;
}

function getHistoryAlertCountValue(item) {
  return toFiniteNumber(item?.with_alerts_count)
    ?? toFiniteNumber(item?.transaction_count)
    ?? 0;
}

function buildUniqueClientsSeries(historyItems, totalUniqueClientsWithAlerts, length) {
  if (!Array.isArray(historyItems) || !historyItems.length)
    return buildSeries((totalUniqueClientsWithAlerts || 0) / Math.max(length, 1), length);

  const directSeries = historyItems.map((item) => Number(item?.unique_clients_with_alerts));
  const hasDirectSeries = directSeries.some((value) => Number.isFinite(value));

  if (hasDirectSeries)
    return directSeries.map((value) => Number(value) || 0);

  const totalAlerts = historyItems.reduce((sum, item) => sum + (Number(item?.with_alerts_count) || 0), 0);

  if (!totalAlerts)
    return buildSeries(0, historyItems.length);

  return historyItems.map((item) => {
    const alerts = Number(item?.with_alerts_count) || 0;
    return Math.round((alerts / totalAlerts) * (Number(totalUniqueClientsWithAlerts) || 0));
  });
}

function formatHistoryLabel(dateValue, groupBy) {
  const isHourGroup = groupBy === 'hour' || groupBy === 'hours';
  const isYearGroup = groupBy === 'year' || groupBy === 'years';
  const isMonthGroup = groupBy === 'month' || groupBy === 'months';
  const safeDate = isHourGroup
    ? parseRangeValue(dateValue)
    : isYearGroup
    ? new Date(`${dateValue}-01-01T00:00:00`)
    : isMonthGroup
      ? new Date(`${dateValue}-01T00:00:00`)
      : new Date(`${dateValue}T00:00:00`);

  if (Number.isNaN(safeDate.getTime()))
    return dateValue;

  if (isYearGroup)
    return safeDate.getFullYear().toString();

  if (isMonthGroup) {
    const monthName = safeDate.toLocaleDateString('pt-BR', { month: 'short' });
    const year = safeDate.getFullYear().toString().slice(-2);
    return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)}. ${year}`;
  }

  if (isHourGroup) {
    const day = safeDate.toLocaleDateString('pt-BR', { day: '2-digit' });
    const month = safeDate.toLocaleDateString('pt-BR', { month: 'short' });
    const hour = safeDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${month} ${day} ${hour}`;
  }

  const day = safeDate.toLocaleDateString('pt-BR', { day: '2-digit' });
  const month = safeDate.toLocaleDateString('pt-BR', { month: 'short' });
  return `${month} ${day}`;
}

function normalizeBucketKey(value, groupBy) {
  const rawValue = String(value || '');

  if (groupBy === 'hour' || groupBy === 'hours') {
    const parsed = parseRangeValue(rawValue);

    if (parsed)
      return formatHourBucket(parsed);

    const rawHour = rawValue.replace('T', ' ').slice(0, 13);
    return `${rawHour}:00:00`;
  }

  if (groupBy === 'year' || groupBy === 'years')
    return rawValue.slice(0, 4);

  if (groupBy === 'month' || groupBy === 'months')
    return rawValue.slice(0, 7);

  return rawValue.slice(0, 10);
}

function generateBucketKeys(startDate, endDate, groupBy) {
  const start = parseRangeValue(startDate, 'start');
  const end = parseRangeValue(endDate, 'end');

  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end)
    return [];

  if (groupBy === 'hour' || groupBy === 'hours') {
    const keys = [];
    const cursor = new Date(start);
    cursor.setMinutes(0, 0, 0);
    const last = new Date(end);

    while (cursor <= last) {
      keys.push(formatHourBucket(cursor));
      cursor.setHours(cursor.getHours() + 1);
    }

    return keys;
  }

  if (groupBy === 'year' || groupBy === 'years') {
    const keys = [];
    for (let year = start.getFullYear(); year <= end.getFullYear(); year += 1)
      keys.push(String(year));
    return keys;
  }

  if (groupBy === 'month' || groupBy === 'months') {
    const keys = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);

    while (cursor <= last) {
      const year = cursor.getFullYear();
      const month = String(cursor.getMonth() + 1).padStart(2, '0');
      keys.push(`${year}-${month}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return keys;
  }

  const keys = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    keys.push(formatDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return keys;
}

// Main highlights
// TODO - Remove block after API integration.
const data = [
  {
    title: AML_DASHBOARD_GRID_TEXT.cards.suspiciousFinancial,
    value: `${Math.floor(Math.random() * 100)}K`,
    variation: `${Math.floor(Math.random() * 40)}%`,
    // interval: 'Last 30 days',
    labels: [],
    trend: 'up',
    data: Array.from(Array(31), () => Math.floor(Math.random() * 1000) + 1),
  },
  {
    title: AML_DASHBOARD_GRID_TEXT.cards.suspiciousQuantity,
    value: `${Math.floor(Math.random() * 100)}K`,
    variation: `${Math.floor(Math.random() * 40)}%`,
    // interval: 'Last 30 days',
    labels: [],
    trend: 'down',
    data: Array.from(Array(31), () => Math.floor(Math.random() * 1000) + 1),
  },
  {
    title: AML_DASHBOARD_GRID_TEXT.cards.suspiciousClients,
    value: `${Math.floor(Math.random() * 100)}K`,
    variation: `${Math.floor(Math.random() * 40)}%`,
    labels: [],
    // interval: 'Last 30 days',
    trend: 'neutral',
    data: Array.from(Array(31), () => Math.floor(Math.random() * 1000) + 1),
  },
];


// Transactions highlights
const transactionsData = {
  title: AML_DASHBOARD_GRID_TEXT.monitoring.quantityTitle,
  description: AML_DASHBOARD_GRID_TEXT.monitoring.description,
  variation: `${Math.floor(Math.random() * 40)}%`,
  value: `R$ ${Math.floor(Math.random() * 10) + 10} M`,
  labels: [],
  transactions: [
    {
      id: 'waiting-analysis',
      label: AML_DASHBOARD_GRID_TEXT.monitoring.labels.waitingAnalysis,
      quantity: Array.from(Array(31), () => Math.floor(Math.random() * 300000) + 1),
      amount: []
    },
    {
      id: 'analysis',
      label: AML_DASHBOARD_GRID_TEXT.monitoring.labels.analysis,
      quantity: Array.from(Array(31), () => Math.floor(Math.random() * 400000) + 1),
      amount: []
    },
    {
      id: 'report',
      label: AML_DASHBOARD_GRID_TEXT.monitoring.labels.report,
      quantity: Array.from(Array(31), () => Math.floor(Math.random() * 500000) + 1),
      amount: []
    },
    {
      id: 'finalized',
      label: AML_DASHBOARD_GRID_TEXT.monitoring.labels.finalized,
      quantity: Array.from(Array(31), () => Math.floor(Math.random() * 600000) + 1),
      amount: []
    }
  ]
};

const transactionsDataFinance = {
  title: AML_DASHBOARD_GRID_TEXT.monitoring.financialTitle,
  description: AML_DASHBOARD_GRID_TEXT.monitoring.description,
  variation: '35%',
  value: `R$ ${Math.floor(Math.random() * 900)} M`,
  labels: Array.from(30, (_, index) => 0 + index * 1),
  transactions: [
    {
      id: 'waiting-analysis',
      label: AML_DASHBOARD_GRID_TEXT.monitoring.labels.waitingAnalysis,
      quantity: Array.from(Array(7), () => Math.floor(Math.random() * 50000) + 1),
      amount: []
    },
    {
      id: 'analysis',
      label: AML_DASHBOARD_GRID_TEXT.monitoring.labels.analysis,
      quantity: Array.from(Array(7), () => Math.floor(Math.random() * 50000) + 1),
      amount: []
    },
    {
      id: 'report',
      label: AML_DASHBOARD_GRID_TEXT.monitoring.labels.report,
      quantity: Array.from(Array(7), () => Math.floor(Math.random() * 50000) + 1),
      amount: []
    },
    {
      id: 'finalized',
      label: AML_DASHBOARD_GRID_TEXT.monitoring.labels.finalized,
      quantity: Array.from(Array(7), () => Math.floor(Math.random() * 50000) + 1),
      amount: []
    }
  ]
};

// Risk highlights
const classificationData = {
  title: AML_DASHBOARD_GRID_TEXT.risk.quantityTitle,
  description: AML_DASHBOARD_GRID_TEXT.risk.description,
  variation: `${Math.floor(Math.random() * 40)}%`,
  value: `${Math.floor(Math.random() * 1000)} Mil`,
  labels: [],
  transactions: [
    {
      id: 'low',
      label: AML_DASHBOARD_GRID_TEXT.risk.labels.low,
      quantity: Array.from(Array(31), () => Math.floor(Math.random() * 10000) + 1),
      amount: []
    },
    {
      id: 'medium',
      label: AML_DASHBOARD_GRID_TEXT.risk.labels.medium,
      quantity: Array.from(Array(31), () => Math.floor(Math.random() * 80000) + 1),
      amount: []
    },
    {
      id: 'high',
      label: AML_DASHBOARD_GRID_TEXT.risk.labels.high,
      quantity: Array.from(Array(31), () => Math.floor(Math.random() * 70000) + 1),
      amount: []
    }
  ]
}

const statusData = {
  title: AML_DASHBOARD_GRID_TEXT.risk.financialTitle,
  description: AML_DASHBOARD_GRID_TEXT.risk.description,
  variation: `${Math.floor(Math.random() * 40)}%`,
  value: `${Math.floor(Math.random() * 500)} M`,
}

export default function AmlDashMainGrid() {
  const canView = canViewTransactions();
  const filter = useSelector((state) => state.filter.value);
  const filterState = useSelector((state) => state.filter);
  const config = getFilterConfig(filter);
  const [overviewData, setOverviewData] = React.useState(null);
  const [financialHistory, setFinancialHistory] = React.useState([]);
  const [analysisStatusHistory, setAnalysisStatusHistory] = React.useState([]);
  const [isLoadingOverview, setIsLoadingOverview] = React.useState(true);
  const [isLoadingFinancialHistory, setIsLoadingFinancialHistory] = React.useState(true);
  const [isLoadingAnalysisStatusHistory, setIsLoadingAnalysisStatusHistory] = React.useState(true);
  const [overviewErrorMessage, setOverviewErrorMessage] = React.useState('');
  const [financialErrorMessage, setFinancialErrorMessage] = React.useState('');
  const [analysisStatusErrorMessage, setAnalysisStatusErrorMessage] = React.useState('');
  const dateRange = React.useMemo(() => getApiDateRangeFromFilterState(filterState), [filterState]);
  const groupBy = React.useMemo(() => getApiGroupByFromFilterState(filterState), [filterState]);

  React.useEffect(() => {
    if (!canView) {
      setOverviewData(null);
      setIsLoadingOverview(false);
      setOverviewErrorMessage('Seu perfil não possui permissão para visualizar o dashboard analítico.');
      return undefined;
    }

    let isMounted = true;

    async function fetchOverview() {
      if (isMounted)
        setIsLoadingOverview(true);
      if (isMounted)
        setOverviewErrorMessage('');

      const rulesUrl = process.env.REACT_APP_API_RULES_URL;

      if (!rulesUrl) {
        if (isMounted) {
          setOverviewData(null);
          setIsLoadingOverview(false);
        }
        return;
      }

      try {
        const payload = await rulesEngineGet(getRulesEngineAnalyticsUrl(rulesUrl, 'overview'), {
          params: {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
          },
        });

        if (isMounted)
          setOverviewData(payload);
      } catch (error) {
        if (isMounted) {
          setOverviewData(null);
          setOverviewErrorMessage(getRulesEngineUiMessage(error));
        }
      } finally {
        if (isMounted)
          setIsLoadingOverview(false);
      }
    }

    fetchOverview();

    return () => {
      isMounted = false;
    };
  }, [canView, dateRange.endDate, dateRange.startDate]);

  React.useEffect(() => {
    if (!canView) {
      setFinancialHistory([]);
      setIsLoadingFinancialHistory(false);
      setFinancialErrorMessage('Seu perfil não possui permissão para visualizar o dashboard analítico.');
      return undefined;
    }

    let isMounted = true;

    async function fetchFinancialHistory() {
      if (isMounted)
        setIsLoadingFinancialHistory(true);
      if (isMounted)
        setFinancialErrorMessage('');

      const rulesUrl = process.env.REACT_APP_API_RULES_URL;

      if (!rulesUrl) {
        if (isMounted) {
          setFinancialHistory([]);
          setIsLoadingFinancialHistory(false);
        }
        return;
      }

      try {
        const payload = await rulesEngineGet(getRulesEngineAnalyticsUrl(rulesUrl, 'financial_volume'), {
          params: {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
            group_by: groupBy,
            only_alerts: 'true',
          },
        });

        if (isMounted)
          setFinancialHistory(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (isMounted) {
          setFinancialHistory([]);
          setFinancialErrorMessage(getRulesEngineUiMessage(error));
        }
      } finally {
        if (isMounted)
          setIsLoadingFinancialHistory(false);
      }
    }

    fetchFinancialHistory();

    return () => {
      isMounted = false;
    };
  }, [canView, dateRange.endDate, dateRange.startDate, groupBy]);

  React.useEffect(() => {
    if (!canView) {
      setAnalysisStatusHistory([]);
      setIsLoadingAnalysisStatusHistory(false);
      setAnalysisStatusErrorMessage('Seu perfil não possui permissão para visualizar o dashboard analítico.');
      return undefined;
    }

    let isMounted = true;

    async function fetchAnalysisStatusHistory() {
      if (isMounted)
        setIsLoadingAnalysisStatusHistory(true);
      if (isMounted)
        setAnalysisStatusErrorMessage('');

      const rulesUrl = process.env.REACT_APP_API_RULES_URL;

      if (!rulesUrl) {
        if (isMounted) {
          setAnalysisStatusHistory([]);
          setIsLoadingAnalysisStatusHistory(false);
        }
        return;
      }

      try {
        const payload = await rulesEngineGet(getRulesEngineAnalyticsUrl(rulesUrl, 'analysis_status_distribution'), {
          params: {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
            group_by: groupBy,
            only_alerts: 'true',
          },
        });

        if (isMounted)
          setAnalysisStatusHistory(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (isMounted) {
          setAnalysisStatusHistory([]);
          setAnalysisStatusErrorMessage(getRulesEngineUiMessage(error));
        }
      } finally {
        if (isMounted)
          setIsLoadingAnalysisStatusHistory(false);
      }
    }

    fetchAnalysisStatusHistory();

    return () => {
      isMounted = false;
    };
  }, [canView, dateRange.endDate, dateRange.startDate, groupBy]);

  const normalizedHistory = React.useMemo(() => {
    if (!Array.isArray(financialHistory) || !financialHistory.length)
      return { items: [], labels: [] };

    const bucketKeys = generateBucketKeys(dateRange.startDate, dateRange.endDate, groupBy);
    const bucketKeySet = new Set(bucketKeys);
    const bucketMap = financialHistory.reduce((accumulator, item) => {
      const key = normalizeBucketKey(item?._id, groupBy);

      if (!bucketKeySet.has(key))
        return accumulator;

      accumulator.set(key, item);
      return accumulator;
    }, new Map());

    const items = bucketKeys.map((key) => bucketMap.get(key) || { _id: key });

    return {
      items,
      labels: bucketKeys.map((key) => formatHistoryLabel(key, groupBy)),
    };
  }, [dateRange.endDate, dateRange.startDate, financialHistory, groupBy]);

  const normalizedAnalysisStatusHistory = React.useMemo(() => {
    if (!Array.isArray(analysisStatusHistory) || !analysisStatusHistory.length)
      return { items: [], labels: [] };

    const bucketKeys = generateBucketKeys(dateRange.startDate, dateRange.endDate, groupBy);
    const bucketKeySet = new Set(bucketKeys);
    const bucketMap = analysisStatusHistory.reduce((accumulator, item) => {
      if (!item || item._id === 'TOTAL')
        return accumulator;

      const key = normalizeBucketKey(item._id, groupBy);

      if (!bucketKeySet.has(key))
        return accumulator;

      accumulator.set(key, item);
      return accumulator;
    }, new Map());

    const items = bucketKeys.map((key) => bucketMap.get(key) || { _id: key, analysis_status: { count: {}, volume_raw: {} } });

    return {
      items,
      labels: bucketKeys.map((key) => formatHistoryLabel(key, groupBy)),
    };
  }, [analysisStatusHistory, dateRange.endDate, dateRange.startDate, groupBy]);

  const historyLabels = normalizedHistory.labels.length ? normalizedHistory.labels : config.labels;
  const statusHistoryLabels = normalizedAnalysisStatusHistory.labels.length
    ? normalizedAnalysisStatusHistory.labels
    : config.labels;
  const isLoadingData = isLoadingOverview || isLoadingFinancialHistory || isLoadingAnalysisStatusHistory;
  const dataErrorMessage = overviewErrorMessage || financialErrorMessage || analysisStatusErrorMessage;

  const cardData = React.useMemo(() => (
    overviewData
      ? [
        {
          ...data[0],
          value: overviewData.financial?.volume_with_alerts || data[0].value,
          variation: overviewData.financial?.alert_volume_percentage_formatted || data[0].variation,
          valueFormatter: formatBrlTooltipValue,
          labels: historyLabels,
          data: normalizedHistory.items.length
            ? normalizedHistory.items.map((item) => getHistoryVolumeValue(item))
            : buildSeries((overviewData.financial?.volume_with_alerts_raw || 0) / Math.max(config.length, 1), config.length),
        },
        {
          ...data[1],
          value: formatCount(overviewData.transactions?.transactions_with_alerts),
          variation: `${overviewData.transactions?.alert_rate_percentage || 0}%`,
          labels: historyLabels,
          data: normalizedHistory.items.length
            ? normalizedHistory.items.map((item) => getHistoryAlertCountValue(item))
            : buildSeries((overviewData.transactions?.transactions_with_alerts || 0) / Math.max(config.length, 1), config.length),
        },
        {
          ...data[2],
          value: formatCount(overviewData.transactions?.unique_clients_with_alerts),
          variation: overviewData.transactions?.unique_clients_alert_rate_percentage_formatted || data[2].variation,
          labels: historyLabels,
          data: buildUniqueClientsSeries(
            normalizedHistory.items,
            overviewData.transactions?.unique_clients_with_alerts,
            historyLabels.length,
          ),
        },
      ]
      : data.map((card) => ({
        ...card,
        valueFormatter: card.title === AML_DASHBOARD_GRID_TEXT.cards.suspiciousFinancial ? formatBrlTooltipValue : undefined,
        labels: config.labels,
        data: Array.from({ length: config.length }, () => Math.floor(Math.random() * 1000) + 1),
      }))
  ), [config.labels, config.length, historyLabels, normalizedHistory.items, overviewData]);

  const statusQuantityData = React.useMemo(() => ({
    ...transactionsData,
    value: overviewData
      ? formatCount(overviewData.transactions?.transactions_with_alerts)
      : transactionsData.value,
    variation: overviewData
      ? `${overviewData.transactions?.alert_rate_percentage || 0}%`
      : transactionsData.variation,
    labels: statusHistoryLabels,
    transactions: transactionsData.transactions.map((transaction) => ({
      ...transaction,
      quantity: normalizedAnalysisStatusHistory.items.length
        ? normalizedAnalysisStatusHistory.items.map((item) => Number(item.analysis_status?.count?.[ANALYSIS_STATUS_BY_MONITORING_CARD_ID[transaction.id]]) || 0)
        : Array.from({ length: config.length }, () => Math.floor(Math.random() * 300000) + 1),
      amount: [],
    })),
  }), [config.length, normalizedAnalysisStatusHistory.items, overviewData, statusHistoryLabels]);

  const statusFinancialData = React.useMemo(() => ({
    ...transactionsDataFinance,
    value: overviewData
      ? overviewData.financial?.volume_with_alerts || transactionsDataFinance.value
      : transactionsDataFinance.value,
    variation: overviewData
      ? overviewData.financial?.alert_volume_percentage_formatted || transactionsDataFinance.variation
      : transactionsDataFinance.variation,
    labels: statusHistoryLabels,
    transactions: transactionsDataFinance.transactions.map((transaction) => ({
      ...transaction,
      quantity: [],
      amount: normalizedAnalysisStatusHistory.items.length
        ? normalizedAnalysisStatusHistory.items.map((item) => Number(item.analysis_status?.volume_raw?.[ANALYSIS_STATUS_BY_MONITORING_CARD_ID[transaction.id]]) || 0)
        : Array.from({ length: config.length }, () => Math.floor(Math.random() * 50000) + 1),
    })),
  }), [config.length, normalizedAnalysisStatusHistory.items, overviewData, statusHistoryLabels]);

  const riskQuantityData = React.useMemo(() => ({
    ...classificationData,
    value: overviewData
      ? formatCount(overviewData.transactions?.transactions_with_alerts)
      : classificationData.value,
    variation: overviewData
      ? `${overviewData.transactions?.alert_rate_percentage || 0}%`
      : classificationData.variation,
    labels: historyLabels,
    transactions: classificationData.transactions.map((transaction) => ({
      ...transaction,
      quantity: normalizedHistory.items.length
        ? normalizedHistory.items.map((item) => Number(item.risk_levels?.count?.[transaction.id]) || 0)
        : overviewData
          ? buildSeries(overviewData.transactions?.risk_levels?.count?.[transaction.id], config.length)
        : Array.from({ length: config.length }, () => Math.floor(Math.random() * 80000) + 1),
      amount: [],
    })),
  }), [config.length, historyLabels, normalizedHistory.items, overviewData]);

  const riskFinancialData = React.useMemo(() => ({
    ...statusData,
    value: overviewData
      ? overviewData.financial?.volume_with_alerts || statusData.value
      : statusData.value,
    variation: overviewData
      ? overviewData.financial?.alert_volume_percentage_formatted || statusData.variation
      : statusData.variation,
    labels: historyLabels,
    transactions: classificationData.transactions.map((transaction) => ({
      id: transaction.id,
      label: transaction.label,
      quantity: [],
      amount: normalizedHistory.items.length
        ? normalizedHistory.items.map((item) => Number(item.risk_levels?.volume_raw?.[transaction.id]) || 0)
        : overviewData
          ? buildSeries(overviewData.transactions?.risk_levels?.volume_raw?.[transaction.id], config.length)
        : Array.from({ length: config.length }, () => Math.floor(Math.random() * 90000) + 1),
    })),
  }), [config.length, historyLabels, normalizedHistory.items, overviewData]);

  if (!canView) {
    return <Alert severity="warning">Seu perfil não possui permissão para visualizar o dashboard analítico.</Alert>;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* cards */}
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        {AML_DASHBOARD_GRID_TEXT.pageTitle}
      </Typography>
      {dataErrorMessage ? (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'error.light' }}>
          <Typography variant="body2" sx={{ color: 'error.contrastText' }}>
            {dataErrorMessage}
          </Typography>
        </Box>
      ) : null}
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(2) }}
      >
        {isLoadingData ? (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ minHeight: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {AML_DASHBOARD_GRID_TEXT.loading}
              </Typography>
            </Box>
          </Grid>
        ) : (
          <>
            {cardData.map((card, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, lg: 4 }}>
                <StatCard {...card} />
              </Grid>
            ))}
            <Grid size={{ xs: 12, md: 6 }}>
              <RiskValueChart {...riskFinancialData} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <RiskQuantityChart {...riskQuantityData } />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MonitoringValueChart {...statusFinancialData} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MonitoringQuantityChart {...statusQuantityData} />
            </Grid>
          </>
        )}
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}
